import { MenuItem, TextField } from '@mui/material';

import {
    Action,
    ActionButton,
    DEFAULT_OPEN_CHARACTER_CREATOR_ACTION,
    DEFAULT_OPEN_CHARACTER_EXPRESSION_ACTION,
    DEFAULT_OPEN_DISTRICT_ACTION,
    DEFAULT_OPEN_IN_STORE_ACTION,
    DEFAULT_OPEN_LOCATION_ACTION,
    DEFAULT_OPEN_OUTFIT_DESIGNER_ACTION,
    DEFAULT_OPEN_PORTAL_ACTION,
    DEFAULT_OPEN_SHOP_ACTION,
} from '../../model/components/ActionButton';
import { PathContextProvider } from '../../PathContext';
import { ActionInput } from './ActionInput';
import { TranslationTableInput } from './TranslationTableInput';
import {
    hexToUnityColor,
    UnityColor,
    unityColorToHex,
} from '@lifeserver/shared';

export const ACTION_OPTIONS: ReadonlyArray<Action['$type']> = [
    'OpenPortal',
    'OpenShop',
    'OpenDistrict',
    'OpenLocation',
    'OpenCharacterCreator',
    'OpenOutfitDesigner',
    'OpenCEX',
    'OpenInStore',
];

const DEFAULT_ACTION_BY_TYPE: Record<Action['$type'], Action> = {
    OpenPortal: DEFAULT_OPEN_PORTAL_ACTION,
    OpenShop: DEFAULT_OPEN_SHOP_ACTION,
    OpenDistrict: DEFAULT_OPEN_DISTRICT_ACTION,
    OpenLocation: DEFAULT_OPEN_LOCATION_ACTION,
    OpenCharacterCreator: DEFAULT_OPEN_CHARACTER_CREATOR_ACTION,
    OpenOutfitDesigner: DEFAULT_OPEN_OUTFIT_DESIGNER_ACTION,
    OpenCEX: DEFAULT_OPEN_CHARACTER_EXPRESSION_ACTION,
    OpenInStore: DEFAULT_OPEN_IN_STORE_ACTION,
};

const SHOP_ACTION_TYPES: ReadonlyArray<Action['$type']> = [
    'OpenShop',
    'OpenInStore',
];

const isShopAction = (actionType: Action['$type']): boolean =>
    SHOP_ACTION_TYPES.includes(actionType);

export interface ActionButtonInputProps {
    value: ActionButton;
    onChange?: (value: ActionButton) => void;
    allowedActionTypes?: ReadonlyArray<Action['$type']>;
}
export const ActionButtonInput = ({
    value,
    onChange,
    allowedActionTypes,
}: ActionButtonInputProps) => {
    const colorsLocked = isShopAction(value.action.$type);
    const canChangeActionType = Array.isArray(allowedActionTypes);
    const visibleActionTypes = allowedActionTypes ?? ACTION_OPTIONS;

    const onFieldChange = <Field extends keyof ActionButton>(
        field: Field,
        fieldValue: ActionButton[Field],
    ) => {
        onChange?.({
            ...value,
            [field]: fieldValue,
        });
    };

    const onColorChange = (
        field: 'backgroundColor' | 'textColor',
        hex: string,
    ) => {
        const color = hexToUnityColor(hex as `#${string}`);
        if (color) {
            onFieldChange(field, { ...color, a: value[field]?.a ?? 1 });
        }
    };

    const toHex = (color: UnityColor | undefined): string => {
        if (!color) return '#ffffff';
        return unityColorToHex(color, false);
    };

    const onActionTypeChange = (actionType: Action['$type']) => {
        onFieldChange('action', { ...DEFAULT_ACTION_BY_TYPE[actionType] });
    };

    return (
        <div>
            <fieldset>
                <legend>Text</legend>
                <PathContextProvider segment="text">
                    <TranslationTableInput
                        value={value.text}
                        onChange={(text) => onFieldChange('text', text)}
                    />
                </PathContextProvider>
            </fieldset>
            <fieldset>
                <legend>
                    Background Color
                    {colorsLocked ? ' (locked for shop buttons)' : ''}
                </legend>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                        type="color"
                        value={toHex(value.backgroundColor)}
                        disabled={colorsLocked}
                        onChange={(e) =>
                            onColorChange('backgroundColor', e.target.value)
                        }
                    />
                    <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
                        {toHex(value.backgroundColor)}
                    </span>
                </div>
            </fieldset>
            <fieldset>
                <legend>
                    Text Color{colorsLocked ? ' (locked for shop buttons)' : ''}
                </legend>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                        type="color"
                        value={toHex(value.textColor)}
                        disabled={colorsLocked}
                        onChange={(e) =>
                            onColorChange('textColor', e.target.value)
                        }
                    />
                    <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
                        {toHex(value.textColor)}
                    </span>
                </div>
            </fieldset>
            <fieldset>
                <legend>Action</legend>
                {canChangeActionType && (
                    <TextField
                        select
                        fullWidth
                        label="Action Type"
                        value={value.action.$type}
                        onChange={(e) =>
                            onActionTypeChange(
                                e.target.value as Action['$type'],
                            )
                        }
                        sx={{ mb: 1 }}
                    >
                        {visibleActionTypes.map((actionType) => (
                            <MenuItem key={actionType} value={actionType}>
                                {actionType}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
                <ActionInput
                    value={value.action}
                    onChange={(action) => onFieldChange('action', action)}
                />
            </fieldset>
        </div>
    );
};
